import { useState } from 'react';
import {
  Drawer,
  Modal,
  Box,
  Title,
  Text,
  Stack,
  Divider,
  Flex,
  Group,
  Button,
  Switch,
  Chip,
  Paper,
  SimpleGrid,
  Select,
  NumberInput,
  SegmentedControl,
  Checkbox,
  Badge,
  Tooltip,
} from '@mantine/core';
import { IconPlus, IconDownload } from '@tabler/icons-react';
import {
  ALL_METRICS,
  Benchmark,
  benchmarkDirection,
  benchmarkMetricRequiresGoal,
  GOAL_METRIC_ORDER,
  GRADE_COLUMNS,
} from './types';
import { LegendDot, metricDisplayLabel } from './shared';
import {
  ASSIST_TOOLTIPS,
  assistMetricsOfferedForGoal,
  withAssistMetrics,
  clearMetrics,
  isAssistMetric,
  selectAllMetrics,
} from './assists';

type SettingsDrawerProps = {
  opened: boolean;
  onClose: () => void;
  onSave?: () => void;
  dirty?: boolean;
  saving?: boolean;
  context: string;
  scope: string;
  visibleMetrics: string[];
  setVisibleMetrics: (m: string[]) => void;
  metricsLabel?: string;
  benchmarks: Benchmark[];
  benchmarksOn: boolean;
  setBenchmarksOn: (v: boolean) => void;
  addBenchmark: () => void;
  removeBenchmark: (id: string) => void;
  updateBenchmark: (id: string, field: keyof Benchmark, value: any) => void;
  // Patches multiple benchmark fields atomically (e.g. switching standalone↔goal).
  setBenchmarkRow?: (id: string, patch: Partial<Benchmark>) => void;
  testVariationVisible?: boolean;
  testVariationActive?: boolean;
  testVariationMessage?: string;
  onToggleTestVariation?: () => void;
  attributionModeVisible?: boolean;
  attributionMode?: 'true_journey' | 'lookback';
  setAttributionMode?: (v: 'true_journey' | 'lookback') => void;
  lookbackDays?: '7' | '30' | '60' | '90' | '365';
  setLookbackDays?: (v: '7' | '30' | '60' | '90' | '365') => void;
  trafficMetrics?: string[];
  setTrafficMetrics?: (m: string[]) => void;
  trafficFunnelMetricsByGoal?: Record<string, string[]>;
  setTrafficFunnelMetricsByGoal?: (m: Record<string, string[]>) => void;
  trafficGoalsEnabled?: Record<string, boolean>;
  setTrafficGoalsEnabled?: (m: Record<string, boolean>) => void;
  trafficGoals?: string[];
  /* Goal name -> whether that goal carries money. Decides which goals may be offered an
     Influenced Revenue column; a $0 goal gets Assisted alone. */
  goalHasRevenue?: Record<string, boolean>;
  benchmarkGoalOptions?: { key: string; name: string }[];
  breakdownType?: 'none' | 'device' | 'hour' | 'cust_type';
  setBreakdownType?: (v: 'none' | 'device' | 'hour' | 'cust_type') => void;
  resetStatsVisible?: boolean;
  onResetStats?: () => void;
  onExport?: () => void;
  exporting?: boolean;
};

export const TRAFFIC_METRICS = [
  'Uniques',
  'Visits',
  'Ad Spend',
  'Network Reported Revenue',
  'Network Conversion',
  'Impressions',
  'CTR',
  'CPC',
  'CPM',
  'Grade',
  'Grade Reason',
];
// Per-goal columns follow the same goal order as the Goal Breakdown; the extras the ad tabs
// add (Profit, ROI, AOV) trail the campaign report's list.
export const TRAFFIC_FUNNEL_METRICS = [
  ...GOAL_METRIC_ORDER,
  'Profit',
  'ROI',
  'AOV',
];

export function SettingsDrawer({
  opened,
  onClose,
  onSave,
  dirty = false,
  saving = false,
  context,
  scope,
  visibleMetrics,
  setVisibleMetrics,
  metricsLabel = 'Visible Metrics',
  benchmarks,
  benchmarksOn,
  setBenchmarksOn,
  addBenchmark,
  removeBenchmark,
  updateBenchmark,
  setBenchmarkRow,
  testVariationVisible = false,
  testVariationActive = false,
  testVariationMessage = '',
  onToggleTestVariation,
  attributionModeVisible = false,
  attributionMode = 'true_journey',
  setAttributionMode,
  lookbackDays = '30',
  setLookbackDays,
  trafficMetrics = [],
  setTrafficMetrics,
  trafficFunnelMetricsByGoal = {},
  setTrafficFunnelMetricsByGoal,
  trafficGoalsEnabled = {},
  setTrafficGoalsEnabled,
  trafficGoals,
  goalHasRevenue = {},
  benchmarkGoalOptions = [],
  breakdownType = 'none',
  setBreakdownType,
  resetStatsVisible = false,
  onResetStats,
  onExport,
  exporting = false,
}: SettingsDrawerProps) {
  const effectiveGoals = trafficGoals && trafficGoals.length > 0 ? trafficGoals : [];
  const isTrafficContext =
    context === 'Campaign Settings' ||
    context === 'Ad Set Settings' ||
    context === 'Ad Settings';
  /* Assisted and Influenced Revenue are an Ad-tab feature, so they are only offered from the
     Ad drawer even though the per-goal selection itself is shared across the three traffic tabs.
     They are listed where the table draws them - right after Conversions - so the chooser reads
     in the same order as the columns. */
  const funnelMetricsForGoal = (goal: string): string[] =>
    context === 'Ad Settings'
      ? withAssistMetrics(TRAFFIC_FUNNEL_METRICS, assistMetricsOfferedForGoal(!!goalHasRevenue[goal]))
      : TRAFFIC_FUNNEL_METRICS;
  /* What this drawer's tab will actually draw for the goal. The selection itself is shared with
     the other traffic tabs, so an assist column chosen here is not counted where it cannot show. */
  const selectedFunnelMetricsForGoal = (goal: string): string[] => {
    const offered = funnelMetricsForGoal(goal);
    return (trafficFunnelMetricsByGoal[goal] ?? []).filter(
      (m) => !isAssistMetric(m) || offered.includes(m)
    );
  };
  // Benchmarks apply only to Total tab; other drawers don't expose them.
  const isBenchmarksContext = context === 'Total Settings';
  const displayTitle = isTrafficContext ? 'Traffic Settings' : context;
  const displayScope = isTrafficContext
    ? context === 'Campaign Settings'
      ? 'Campaign · Ad Set · Ad · Funnel Traffic Breakdown'
      : context === 'Ad Set Settings'
      ? 'Ad Set · Ad · Funnel Traffic Breakdown'
      : 'Ad · Funnel Traffic Breakdown'
    : scope;

  // Turning Test Variation on with campaigns that have no test explains itself first; the
  // change is only staged, so Save still applies it.
  const [tvConfirmOpen, setTvConfirmOpen] = useState(false);
  const handleTestVariationChange = () => {
    if (!testVariationActive && testVariationMessage !== '') {
      setTvConfirmOpen(true);
      return;
    }
    onToggleTestVariation?.();
  };

  // A saved view can still hold a metric that has since become a structural column, so every
  // write drops anything the current list no longer offers.
  const knownTrafficMetrics = trafficMetrics.filter((m) => TRAFFIC_METRICS.includes(m));
  const toggleTrafficMetric = (m: string) => {
    if (!setTrafficMetrics) return;
    // Grade is unreadable without its reason, so the two columns toggle as one.
    const group = GRADE_COLUMNS.includes(m) ? GRADE_COLUMNS : [m];
    const rest = knownTrafficMetrics.filter((x) => !group.includes(x));
    setTrafficMetrics(knownTrafficMetrics.includes(m) ? rest : [...rest, ...group]);
  };
  const toggleFunnelMetric = (goal: string, m: string) => {
    if (!setTrafficFunnelMetricsByGoal) return;
    const current = trafficFunnelMetricsByGoal[goal] ?? [];
    const next = current.includes(m)
      ? current.filter((x) => x !== m)
      : [...current, m];
    setTrafficFunnelMetricsByGoal({ ...trafficFunnelMetricsByGoal, [goal]: next });
  };
  const setGoalEnabled = (goal: string, enabled: boolean) => {
    if (!setTrafficGoalsEnabled) return;
    setTrafficGoalsEnabled({ ...trafficGoalsEnabled, [goal]: enabled });
  };

  const activeColumnsCount =
    knownTrafficMetrics.length +
    effectiveGoals.reduce(
      (sum, g) =>
        sum + ((trafficGoalsEnabled[g] ?? true) ? selectedFunnelMetricsForGoal(g).length : 0),
      0
    );
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Box>
          <Title order={4}>{displayTitle}</Title>
          <Text size="xs" color="dimmed" mt={4}>
            {displayScope}
          </Text>
        </Box>
      }
      position="right"
      size={isTrafficContext ? 540 : 460}
      padding="md"
      styles={{
        content: { display: 'flex', flexDirection: 'column' },
        body:    { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0 },
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', padding: 'var(--mantine-spacing-md, 16px)' }}>
      <Stack spacing="lg">
        {isTrafficContext && (
          <>
            <Paper
              withBorder
              radius="md"
              p="md"
              sx={(t) => ({
                borderColor: t.colors.teal[t.colorScheme === 'dark' ? 6 : 5],
                backgroundColor:
                  t.colorScheme === 'dark'
                    ? t.fn.rgba(t.colors.teal[8], 0.12)
                    : t.colors.teal[0],
              })}
            >
              <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
                <Group spacing="sm">
                  <Text
                    size="xs"
                    weight={700}
                    transform="uppercase"
                    sx={(t) => ({
                      letterSpacing: 0.5,
                      color: t.colors.teal[t.colorScheme === 'dark' ? 3 : 7],
                    })}
                  >
                    Active Columns
                  </Text>
                  <Text
                    size="xl"
                    weight={700}
                    sx={(t) => ({
                      color: t.colors.teal[t.colorScheme === 'dark' ? 3 : 7],
                    })}
                  >
                    {activeColumnsCount}
                  </Text>
                </Group>
                <Text size="xs" color="dimmed">
                  scroll table to see all
                </Text>
              </Flex>
            </Paper>

            <Box>
              <Text
                size="xs"
                color="dimmed"
                transform="uppercase"
                weight={700}
                sx={{ letterSpacing: 0.5 }}
                mb="xs"
              >
                Breakdown
              </Text>
              <Text size="xs" color="dimmed" mb="sm">
                Group Campaign / Ad Set / Ad rows by device type or hour of day.
              </Text>
              <Select
                value={breakdownType}
                onChange={(v) => setBreakdownType?.((v as 'none' | 'device' | 'hour' | 'cust_type') || 'none')}
                data={[
                  { value: 'none', label: 'None' },
                  { value: 'device', label: 'Device Type' },
                  { value: 'hour', label: 'Time Breakdown' },
                  { value: 'cust_type', label: 'Customer Type' },
                ]}
                allowDeselect={false}
                maw={260}
              />
            </Box>


            <Box>
              <Flex justify="space-between" align="center" mb="sm">
                <Text
                  size="xs"
                  color="dimmed"
                  transform="uppercase"
                  weight={700}
                  sx={{ letterSpacing: 0.5 }}
                >
                  Traffic Metrics
                </Text>
                <Group spacing={6}>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => setTrafficMetrics?.([...TRAFFIC_METRICS])}
                  >
                    All
                  </Button>
                  <Button size="xs" variant="default" onClick={() => setTrafficMetrics?.([])}>
                    None
                  </Button>
                </Group>
              </Flex>
              <SimpleGrid cols={2} spacing="xs">
                {TRAFFIC_METRICS.map((m) => (
                  <MetricCard
                    key={m}
                    label={metricDisplayLabel(m)}
                    checked={trafficMetrics.includes(m)}
                    onToggle={() => toggleTrafficMetric(m)}
                  />
                ))}
              </SimpleGrid>
            </Box>

            <Box>
              <Text
                size="xs"
                color="dimmed"
                transform="uppercase"
                weight={700}
                sx={{ letterSpacing: 0.5 }}
              >
                Traffic Funnel Metrics
              </Text>
              <Text size="xs" color="dimmed" mt={4} mb="sm">
                Select metrics per goal · columns append after traffic metrics
              </Text>
              <Stack spacing="md">
                {effectiveGoals.map((goal) => {
                  const enabled = trafficGoalsEnabled[goal] ?? true;
                  const goalMetrics = selectedFunnelMetricsForGoal(goal);
                  return (
                    <Paper
                      key={goal}
                      withBorder
                      radius="md"
                      p="md"
                      sx={(t) => ({
                        backgroundColor:
                          t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
                      })}
                    >
                      <Flex justify="space-between" align="center" wrap="wrap" gap="sm" mb="sm">
                        <Group spacing="xs">
                          <Text weight={600} size="sm">
                            {goal}
                          </Text>
                          <Badge
                            color="teal"
                            variant="outline"
                            size="sm"
                            sx={{ textTransform: 'none' }}
                          >
                            {goalMetrics.length} metrics
                          </Badge>
                        </Group>
                        <Group spacing={6}>
                          <Button
                            size="xs"
                            variant="default"
                            disabled={!enabled}
                            onClick={() =>
                              setTrafficFunnelMetricsByGoal?.({
                                ...trafficFunnelMetricsByGoal,
                                [goal]: selectAllMetrics(
                                  funnelMetricsForGoal(goal),
                                  trafficFunnelMetricsByGoal[goal] ?? []
                                ),
                              })
                            }
                          >
                            All
                          </Button>
                          <Button
                            size="xs"
                            variant="default"
                            disabled={!enabled}
                            onClick={() =>
                              setTrafficFunnelMetricsByGoal?.({
                                ...trafficFunnelMetricsByGoal,
                                [goal]: clearMetrics(
                                  funnelMetricsForGoal(goal),
                                  trafficFunnelMetricsByGoal[goal] ?? []
                                ),
                              })
                            }
                          >
                            None
                          </Button>
                          <Switch
                            checked={enabled}
                            onChange={(e) => setGoalEnabled(goal, e.currentTarget.checked)}
                            color="teal"
                            size="md"
                          />
                        </Group>
                      </Flex>
                      <SimpleGrid cols={2} spacing="xs">
                        {funnelMetricsForGoal(goal).map((m) => (
                          <MetricCard
                            key={m}
                            label={metricDisplayLabel(m)}
                            /* The assist columns are new and easy to confuse with credited
                               conversions, so the chooser carries the same explanation the
                               column header does. */
                            hint={isAssistMetric(m) ? ASSIST_TOOLTIPS[m] : undefined}
                            checked={enabled && goalMetrics.includes(m)}
                            disabled={!enabled}
                            onToggle={() => toggleFunnelMetric(goal, m)}
                          />
                        ))}
                      </SimpleGrid>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </>
        )}

        {!isTrafficContext && attributionModeVisible && (
          <Box>
            <Text
              size="xs"
              color="dimmed"
              transform="uppercase"
              weight={700}
              mb="sm"
              sx={{ letterSpacing: 0.5 }}
            >
              Attribution Mode
            </Text>
            <SegmentedControl
              fullWidth
              color="teal"
              value={attributionMode}
              onChange={(v: any) => setAttributionMode?.(v)}
              data={[
                { label: 'True Customer Journey', value: 'true_journey' },
                { label: 'Lookback Window', value: 'lookback' },
              ]}
            />
            {attributionMode === 'lookback' && (
              <Box mt="sm">
                <Select
                  label="Lookback Window"
                  value={lookbackDays}
                  onChange={(v) => v && setLookbackDays?.(v as '7' | '30' | '60' | '90' | '365')}
                  data={[
                    { value: '7', label: '7 days' },
                    { value: '30', label: '30 days' },
                    { value: '60', label: '60 days' },
                    { value: '90', label: '90 days' },
                    { value: '365', label: '365 days' },
                  ]}
                  withinPortal
                  dropdownPosition="bottom"
                />
              </Box>
            )}
          </Box>
        )}

        {!isTrafficContext && attributionModeVisible && <Divider />}

        {!isTrafficContext && testVariationVisible && (
          <Box>
            <Flex justify="space-between" align="center" mb="xs">
              <Text
                size="xs"
                color="dimmed"
                transform="uppercase"
                weight={700}
                sx={{ letterSpacing: 0.5 }}
              >
                Test Variation
              </Text>
              <Switch
                checked={testVariationActive}
                onChange={handleTestVariationChange}
                color="teal"
                size="md"
                onLabel="ON"
                offLabel="OFF"
              />
            </Flex>
          </Box>
        )}

        {!isTrafficContext && testVariationVisible && <Divider />}

        {!isTrafficContext && (
        <Box>
          <Text
            size="xs"
            color="dimmed"
            transform="uppercase"
            weight={700}
            mb="sm"
            sx={{ letterSpacing: 0.5 }}
          >
            {metricsLabel}
          </Text>
          <Chip.Group
            multiple
            value={visibleMetrics}
            onChange={(v: any) => {
              // Grade / Grade Reason are traffic-only columns; they never enter this list.
              setVisibleMetrics((v as string[]).filter((m) => !GRADE_COLUMNS.includes(m)));
            }}
          >
            <Flex gap="xs" wrap="wrap">
              {ALL_METRICS.map((m) => (
                <Chip key={m} value={m} color="teal" variant="outline">
                  {m}
                </Chip>
              ))}
            </Flex>
          </Chip.Group>
        </Box>
        )}

        {isBenchmarksContext && <Divider />}

        {isBenchmarksContext && (
        <Box>
          <Flex justify="space-between" align="center" mb="xs">
            <Text
              size="xs"
              color="dimmed"
              transform="uppercase"
              weight={700}
              sx={{ letterSpacing: 0.5 }}
            >
              Benchmarks
            </Text>
            <Group spacing="xs">
              <Button
                variant="outline"
                size="xs"
                color="teal"
                leftIcon={<IconPlus size="0.9rem" />}
                onClick={addBenchmark}
              >
                Add
              </Button>
              <Switch
                checked={benchmarksOn}
                onChange={(e) => setBenchmarksOn(e.currentTarget.checked)}
                color="teal"
                size="md"
                onLabel="ON"
                offLabel="OFF"
              />
            </Group>
          </Flex>
          <Text size="xs" color="dimmed" mb="md">
            Goal is required for per-goal metrics (CPA, ROAS, Revenue, …); standalone metrics
            (Spend, Visits, Uniques) apply across the report.
          </Text>

          <Stack spacing="md">
            {benchmarks.map((b) => {
              const requiresGoal = benchmarkMetricRequiresGoal(b.metric);
              const direction = b.metric ? benchmarkDirection(b.metric) : 'higher';
              const isLower = direction === 'lower';
              const onMetricChange = (next: string) => {
                const nextRequiresGoal = benchmarkMetricRequiresGoal(next);
                if (setBenchmarkRow) {
                  setBenchmarkRow(b.id, nextRequiresGoal ? { metric: next } : { metric: next, goal: '', goal_key: '' });
                  return;
                }
                updateBenchmark(b.id, 'metric', next);
                if (!nextRequiresGoal) {
                  if (b.goal)     { updateBenchmark(b.id, 'goal', ''); }
                  if (b.goal_key) { updateBenchmark(b.id, 'goal_key', ''); }
                }
              };
              const onGoalKeyChange = (nextKey: string) => {
                const match = benchmarkGoalOptions.find((o) => o.key === nextKey);
                const nextName = match ? match.name : '';
                if (setBenchmarkRow) {
                  setBenchmarkRow(b.id, { goal_key: nextKey, goal: nextName });
                  return;
                }
                updateBenchmark(b.id, 'goal_key', nextKey);
                updateBenchmark(b.id, 'goal', nextName);
              };
              return (
              <Paper
                key={b.id}
                withBorder
                radius="md"
                p="md"
                sx={(theme) => ({
                  backgroundColor:
                    theme.colorScheme === 'dark'
                      ? theme.colors.dark[6]
                      : theme.colors.gray[0],
                })}
              >
                <SimpleGrid cols={requiresGoal ? 2 : 1} spacing="sm" mb="sm">
                  <Select
                    label="Metric"
                    placeholder="Select metric"
                    value={b.metric || null}
                    onChange={(v) => onMetricChange(v || '')}
                    data={ALL_METRICS.map((m) => ({ value: m, label: m }))}
                    searchable
                    withinPortal
                    dropdownPosition="bottom"
                  />
                  {requiresGoal && (() => {
                    // Picker is keyed by goal_key (ID); label shows the display name.
                    // Fall back to filtering by name for legacy rows saved before goal_key.
                    const fallbackKey = !b.goal_key && b.goal
                      ? (benchmarkGoalOptions.find((o) => o.name === b.goal)?.key ?? '')
                      : '';
                    const currentKey = b.goal_key || fallbackKey;
                    return (
                      <Select
                        label="Goal"
                        placeholder={benchmarkGoalOptions.length === 0 ? 'No goals available' : 'Select goal'}
                        value={currentKey || null}
                        onChange={(v) => onGoalKeyChange(v || '')}
                        data={benchmarkGoalOptions.map((o) => ({ value: o.key, label: o.name }))}
                        disabled={benchmarkGoalOptions.length === 0}
                        searchable
                        withinPortal
                        dropdownPosition="bottom"
                      />
                    );
                  })()}
                </SimpleGrid>

                {b.metric && (
                  <Flex justify="flex-end" mb="xs">
                    <Badge color="gray" variant="light" size="xs">
                      {isLower ? '↓ Lower is better' : '↑ Higher is better'}
                    </Badge>
                  </Flex>
                )}

                {/* Values between Good and Poor are backend-graded as Watch. */}
                <Stack spacing="xs">
                  <Flex align="center" gap="sm">
                    <Text size="sm" sx={{ width: 120, color: '#22c55e' }}>
                      <LegendDot color="#22c55e" /> Good if {isLower ? '≤' : '≥'}
                    </Text>
                    <NumberInput
                      value={b.good}
                      onChange={(v) => updateBenchmark(b.id, 'good', v ?? 0)}
                      precision={2}
                      hideControls
                      sx={{ flex: 1 }}
                    />
                  </Flex>
                  <Flex align="center" gap="sm">
                    <Text size="sm" sx={{ width: 120, color: '#ef4444' }}>
                      <LegendDot color="#ef4444" /> Poor if {isLower ? '≥' : '≤'}
                    </Text>
                    <NumberInput
                      value={b.poor}
                      onChange={(v) => updateBenchmark(b.id, 'poor', v ?? 0)}
                      precision={2}
                      hideControls
                      sx={{ flex: 1 }}
                    />
                  </Flex>
                  <Text size="xs" color="dimmed">
                    <LegendDot color="#f59e0b" /> Watch — values between Good and Poor thresholds.
                  </Text>
                </Stack>

                <Flex justify="flex-end" mt="sm">
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => removeBenchmark(b.id)}
                  >
                    Remove
                  </Button>
                </Flex>
              </Paper>
              );
            })}
          </Stack>
        </Box>
        )}

        {onExport && (
        <Box mt="md">
          <Flex justify="space-between" align="center" mb="xs">
            <Text
              size="sm"
              transform="uppercase"
              weight={700}
              sx={{ letterSpacing: 0.5 }}
            >
              Export
            </Text>
            <Button
              variant="default"
              size="xs"
              leftIcon={<IconDownload size="0.9rem" />}
              onClick={onExport}
              loading={exporting}
            >
              Export CSV
            </Button>
          </Flex>
          <Text size="xs" color="dimmed">
            Downloads this tab with every metric column, under the report's current filters,
            attribution and date range.
          </Text>
        </Box>
        )}

        {resetStatsVisible && onResetStats && <Divider mt="xl" />}

        {resetStatsVisible && onResetStats && (
        <Box mt="md">
          <Flex justify="space-between" align="center" mb="xs">
            <Text
              size="sm"
              transform="uppercase"
              weight={700}
              sx={{ letterSpacing: 0.5 }}
            >
              Reset
            </Text>
            <Button color="red" size="xs" onClick={onResetStats}>
              Reset Stats
            </Button>
          </Flex>
          <Text size="xs" color="dimmed">
            Permanently deletes stats for the selected Visiopt campaigns. This affects all reports
            using those campaigns.
          </Text>
        </Box>
        )}
      </Stack>
      </Box>
      {onSave && (
        <Box
          sx={(t) => ({
            borderTop: `1px solid ${t.colorScheme === 'dark' ? t.colors.dark[4] : t.colors.gray[3]}`,
            padding: 12,
            background: t.colorScheme === 'dark' ? t.colors.dark[7] : t.white,
          })}
        >
          <Flex justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button color="teal" onClick={onSave} disabled={!dirty || saving} loading={saving}>
              Save
            </Button>
          </Flex>
        </Box>
      )}

      <Modal
        opened={tvConfirmOpen}
        onClose={() => setTvConfirmOpen(false)}
        title="Test Variation"
        centered
        size="sm"
      >
        <Stack spacing="md">
          <Text size="sm">{testVariationMessage}</Text>
          <Flex justify="flex-end">
            <Button
              color="teal"
              onClick={() => {
                setTvConfirmOpen(false);
                onToggleTestVariation?.();
              }}
            >
              OK
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </Drawer>
  );
}

function MetricCard({
  label,
  checked,
  onToggle,
  disabled = false,
  hint,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  hint?: string;
}) {
  const card = (
    <Box
      onClick={disabled ? undefined : onToggle}
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 6,
        border: `1px solid ${
          checked
            ? t.colors.teal[t.colorScheme === 'dark' ? 6 : 5]
            : t.colorScheme === 'dark'
            ? t.colors.dark[4]
            : t.colors.gray[3]
        }`,
        backgroundColor: checked
          ? t.colorScheme === 'dark'
            ? t.fn.rgba(t.colors.teal[8], 0.18)
            : t.colors.teal[0]
          : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        userSelect: 'none',
        transition: 'background-color 120ms ease, border-color 120ms ease',
      })}
    >
      <Checkbox
        checked={checked}
        readOnly
        color="teal"
        size="xs"
        disabled={disabled}
        sx={{ pointerEvents: 'none' }}
      />
      <Text size="sm" weight={checked ? 600 : 500}>
        {label}
      </Text>
    </Box>
  );
  if (!hint) { return card; }
  return (
    <Tooltip label={hint} multiline width={320} withArrow position="top" openDelay={250}>
      {card}
    </Tooltip>
  );
}
